import React from "react";
import format from "date-fns/format";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import isWithinInterval from "date-fns/isWithinInterval";
import { BloxButton } from "styled_components/Common/BloxButton";
import { BillingUtils, INVOICES_STATUS } from "services/billing";
import { Utils } from "services/utils";

const findCurrentInvoice = (invoices) => {
  const today = new Date();
  const invoicesDueToday =
    invoices &&
    invoices.filter((invoice) => {
      const invoiceStart = startOfDay(new Date(invoice?.billcycle));
      const invoiceEnd = endOfDay(new Date(invoice?.dueDate));

      if (invoice?.type === "Overdue") {
        return false;
      } else {
        return isWithinInterval(today, {
          start: invoiceStart,
          end: invoiceEnd,
        });
      }
    });

  return invoicesDueToday && invoicesDueToday[0] ? invoicesDueToday[0] : null;
};

const generateSummaryBalanceCopy = ({ balance }) => {
  const unknownBalanceEl = (
    <div className="summary-balance">Balance Unknown</div>
  );
  if (!balance) {
    return unknownBalanceEl;
  }

  const regex = /(\d*)\.?(\d*)/;
  const matches = balance.toString().match(regex);
  const [, dollars, cents] = matches;
  let amount = Utils.formatCurrency(dollars, true);
  amount = amount.split(".");
  if (dollars && cents) {
    return (
      <>
        <span className="dollar-sign numbers10">$</span>
        <span className="dollars numbers30">{amount[0]}</span>
        <span className="cents numbers10">{cents}</span>
      </>
    );
  }

  return unknownBalanceEl;
};

const generateDueTodayCopy = (summary, splitInvoices) => {
  const { amountDueToday } = summary;
  const { amountOverdue } = summary;
  let latestOverdueDate = "??.??.??";

  if (amountOverdue > 0) {
    const sortedOverdueInvoices = BillingUtils.sortDueDate(
      splitInvoices.overdue
    ).reverse();
    if (sortedOverdueInvoices[0]) {
      latestOverdueDate = format(
        new Date(sortedOverdueInvoices[0].due),
        "MM.dd.yy"
      );
    }
  }

  return (
    <span className="due-today-amount">
      {`$${Utils.formatCurrency(amountDueToday)}`}
      <span className="total-amount-due-subtext-bold"> due today.</span>
      {amountOverdue > 0 && (
        <span className="amount-overdue due-today-more-detail">
          {` $${Utils.formatCurrency(
            amountOverdue
          )} due on ${latestOverdueDate}.`}
        </span>
      )}
    </span>
  );
};

const generateOverdueCopy = (summary) => {
  const { amountOverdue } = summary;

  return (
    <span className="overdue-amount">
      {`$${Utils.formatCurrency(amountOverdue)}`}
      <span className="total-amount-due-subtext-bold"> past due. </span>
    </span>
  );
};

const generateNextPaymentDateCopy = (nextBillingDate) => {
  if (!nextBillingDate) return null;
  const BillingDate = format(new Date(nextBillingDate), "MM.dd.yy");
  return (
    <span className="next-payment">
      Next payment due&nbsp;
      <span className="next-payment-due-date">{BillingDate}</span>
    </span>
  );
};

const generateCurrentInvoiceCopy = (currentInvoice) => {
  if (!currentInvoice) return "";

  const formattedCurrentInvoiceDate = format(
    new Date(currentInvoice.due),
    "MM.dd.yy"
  );
  const currentInvoiceDue = currentInvoice.amount_due > 0;

  return (
    currentInvoiceDue &&
    `$${currentInvoice.amount_due} due ${formattedCurrentInvoiceDate}`
  );
};

const getCallToActionCopy = (splitInvoices, status, summary) => {
  const currentInvoice = splitInvoices && findCurrentInvoice(splitInvoices.all);
  const summaryCopy = generateSummaryBalanceCopy(summary);
  const summaryBalance = summary.balance ? parseFloat(summary.balance) : null;
  let statusCopy = null;
  let nextPaymentCopy = null;

  if (status === INVOICES_STATUS.DUE_TODAY) {
    statusCopy = generateDueTodayCopy(summary, splitInvoices);
  } else if (status === INVOICES_STATUS.OVERDUE || summary.amountOverdue > 0) {
    statusCopy = generateOverdueCopy(summary);
    nextPaymentCopy = generateCurrentInvoiceCopy(currentInvoice);
  } else if (summaryBalance > 0) {
    statusCopy = generateNextPaymentDateCopy(summary.nextBillingDate);
  } else if (summaryBalance === 0 && summary.nextBillingDate) {
    const nextBillingDate = format(
      new Date(summary.nextBillingDate),
      "MM.dd.yy"
    );
    statusCopy = `Your account is up to date.`;
    nextPaymentCopy = `Next invoice is on ${nextBillingDate}`;
  } else if (summaryBalance === 0 && !summary.nextBillingDate) {
    statusCopy = "No payment due.";
  }

  return { summaryCopy, statusCopy, nextPaymentCopy };
};

const CallToAction = ({
  invoices,
  summary,
  hasOnlinePaymentAccess,
  goToMakeAPayment,
}) => {
  const customClass = hasOnlinePaymentAccess ? "" : "no-button";
  if (!summary) {
    return (
      <div className="call-to-action">
        <div className={`balance-summary ${customClass}`}>
          <span className="dollar-sign numbers10">$</span>
          <span className="dollars numbers30">0</span>
          <span className="cents numbers10">00</span>
        </div>
      </div>
    );
  }

  if (!invoices) {
    const { summaryCopy, statusCopy, nextPaymentCopy } = getCallToActionCopy(
      null,
      summary?.status,
      summary
    );
    return (
      <div className="call-to-action">
        <div className={`balance-summary ${customClass}`}>{summaryCopy}</div>
        <div className="sub-text-section">
          <span className="balance-action total-amount-due-subtext">
            {statusCopy}
          </span>
          {nextPaymentCopy && (
            <span className="next-invoice total-amount-due-subtext">
              {nextPaymentCopy}
            </span>
          )}
        </div>
      </div>
    );
  } else {
    const { invoices: splitInvoices = [], status } =
      BillingUtils.getInvoiceStatus(invoices);

    const { summaryCopy, statusCopy, nextPaymentCopy } =
      invoices && getCallToActionCopy(splitInvoices, status, summary);
    return (
      <div className="call-to-action">
        <div className={`balance-summary ${customClass}`}>{summaryCopy}</div>
        <div className="sub-text-section">
          <span className="balance-action total-amount-due-subtext">
            {statusCopy}
          </span>
          {nextPaymentCopy && (
            <span className="next-invoice total-amount-due-subtext">
              {nextPaymentCopy}
            </span>
          )}
        </div>

        {hasOnlinePaymentAccess && (
          <BloxButton
            className="buttons41"
            id="okay"
            text="PAY NOW"
            module="billing"
            enabled={true}
            onClick={goToMakeAPayment}
            cypressId="pay-now"
          />
        )}
      </div>
    );
  }

  return (
    <div className="call-to-action">
      <div className={`balance-summary ${customClass}`}>{summaryCopy}</div>
      <div className="sub-text-section">
        <span className="balance-action total-amount-due-subtext">
          {statusCopy}
        </span>
        {nextPaymentCopy && (
          <span className="next-invoice total-amount-due-subtext">
            {nextPaymentCopy}
          </span>
        )}
      </div>

      {hasOnlinePaymentAccess && (
        <BloxButton
          className="buttons41"
          id="okay"
          text="PAY NOW"
          module="billing"
          enabled={true}
          onClick={goToMakeAPayment}
          cypressId="pay-now"
        />
      )}
    </div>
  );
};

export default CallToAction;
